/**
 * 💎 HBT TOKEN SHOP - La boutique magique de Heartbeats
 * 
 * Mode Mozart-Tesla activé ! ⚡🎵
 * 
 * "L'énergie suit l'intention" - Nikola Tesla
 * "La musique est la langue des émotions" - Mozart
 * 
 * Cette boutique unit technologie et émotion pour créer
 * une expérience d'achat transcendante !
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, Gift, Crown, Sparkles, X, ArrowRight, ArrowLeftRight,
  Star, Zap, Heart, MessageCircle, Users, Lock, ChevronRight,
  TrendingUp, History, Send, Check
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { toast } from 'sonner';
import { api } from '../../../services/api';

// 💎 Constantes
const CROWNS_PER_HBT = 100;
const HBT_VALUE_EUR = 0.10;

// 🎨 Couleurs des tiers
const TIER_COLORS = {
  bronze: 'from-amber-600 to-orange-700',
  silver: 'from-gray-400 to-slate-500',
  gold: 'from-yellow-400 to-amber-500',
  diamond: 'from-cyan-400 to-blue-500'
};

const TIER_GLOW = {
  bronze: 'shadow-amber-500/30',
  silver: 'shadow-gray-400/30',
  gold: 'shadow-yellow-400/50',
  diamond: 'shadow-cyan-400/50'
};

// ============================================
// 💰 WALLET CARD - Affichage du solde
// ============================================
export const WalletCard = ({ wallet, isDarkMode = true, onOpenShop }) => {
  if (!wallet) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl p-4 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-500/30' 
          : 'bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-300'
      }`}
    >
      {/* Effet lumineux */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-2xl" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Mon Wallet HBT
              </h4>
              <p className={`text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                💎 Heartbeat Token
              </p>
            </div>
          </div>
          <Button
            onClick={onOpenShop}
            size="sm"
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90"
          >
            Boutique 🛒
          </Button>
        </div>

        {/* Balances */}
        <div className="grid grid-cols-3 gap-3">
          {/* HBT */}
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-black/30' : 'bg-white/50'}`}>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-lg">💎</span>
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>HBT</span>
            </div>
            <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {(wallet.balance_hbt || 0).toFixed(1)}
            </p>
          </div>

          {/* Couronnes */}
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-black/30' : 'bg-white/50'}`}>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-lg">👑</span>
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Couronnes</span>
            </div>
            <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {wallet.balance_crowns || 0}
            </p>
          </div>

          {/* Valeur EUR */}
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-black/30' : 'bg-white/50'}`}>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-lg">💶</span>
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Valeur</span>
            </div>
            <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {(wallet.balance_eur || 0).toFixed(2)}€
            </p>
          </div>
        </div>

        {/* Quick stats */}
        <div className={`mt-3 flex items-center justify-between text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span>🎁 {wallet.referrals_count || 0} filleuls</span>
          <span>📈 {(wallet.total_earned || 0).toFixed(1)} HBT gagnés</span>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// 🎁 GIFT CARD - Carte cadeau
// ============================================
const GiftCard = ({ gift, onSelect, isDarkMode, disabled }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => !disabled && onSelect(gift)}
      disabled={disabled}
      className={`relative overflow-hidden rounded-xl p-4 text-left transition-all ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'} 
      border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}
      shadow-lg ${TIER_GLOW[gift.tier]}`}
    >
      {/* Gradient overlay */}
      <div className={`absolute inset-0 bg-gradient-to-br ${TIER_COLORS[gift.tier]} opacity-10`} />
      
      <div className="relative z-10">
        {/* Emoji & Name */}
        <div className="flex items-start justify-between mb-2">
          <span className="text-3xl">{gift.emoji}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r ${TIER_COLORS[gift.tier]} text-white`}>
            {gift.tier}
          </span>
        </div>
        
        <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {gift.name}
        </h4>
        
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {gift.description}
        </p>
        
        {/* Price */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {gift.price_hbt} 💎
            </span>
            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              ({gift.price_eur_display})
            </span>
          </div>
          <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        </div>
        
        {/* Bonus indicator */}
        {gift.bonus_badge && (
          <div className={`mt-2 text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
            ✨ +Badge {gift.bonus_badge}
          </div>
        )}
        {gift.bonus_highlight_hours && (
          <div className={`mt-2 text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
            ⭐ +{gift.bonus_highlight_hours}h de mise en avant
          </div>
        )}
      </div>
    </motion.button>
  );
};

// ============================================
// 🏪 SHOP MODAL - La boutique principale
// ============================================
export const HBTShopModal = ({ 
  isOpen, 
  onClose, 
  customerId,
  targetProfile = null, // Pour envoyer un cadeau à quelqu'un
  isDarkMode = true 
}) => {
  const [wallet, setWallet] = useState(null);
  const [gifts, setGifts] = useState({});
  const [activeTab, setActiveTab] = useState('gifts');
  const [activeTier, setActiveTier] = useState('all');
  const [selectedGift, setSelectedGift] = useState(null);
  const [giftMessage, setGiftMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [crownsToConvert, setCrownsToConvert] = useState('');
  const [loading, setLoading] = useState(true);

  // Charger les données
  useEffect(() => {
    if (isOpen && customerId) {
      loadData();
    }
  }, [isOpen, customerId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [walletRes, giftsRes, transRes] = await Promise.all([
        api.getHBTWallet(customerId),
        api.getHBTGiftsCatalog(),
        api.getHBTTransactions(customerId)
      ]);
      
      setWallet(walletRes.data);
      setGifts(giftsRes.data.gifts || {});
      setTransactions(transRes.data.transactions || []);
    } catch (error) {
      console.error('Erreur chargement boutique:', error);
      toast.error('Erreur de chargement');
    }
    setLoading(false);
  };

  // Envoyer un cadeau
  const handleSendGift = async () => {
    if (!selectedGift || !targetProfile) return;
    
    setSending(true);
    try {
      await api.sendHBTGift({
        senderId: customerId,
        receiverId: targetProfile.customerId,
        giftId: selectedGift.id,
        message: giftMessage
      });
      
      toast.success(`🎁 ${selectedGift.emoji} ${selectedGift.name} envoyé à ${targetProfile.displayName} !`);
      setSelectedGift(null);
      setGiftMessage('');
      loadData(); // Refresh wallet
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setSending(false);
  };

  // Convertir des couronnes en HBT
  const handleConvert = async () => {
    const amount = parseInt(crownsToConvert);
    if (isNaN(amount) || amount < CROWNS_PER_HBT) {
      toast.error(`Minimum ${CROWNS_PER_HBT} couronnes`);
      return;
    }

    try {
      const res = await api.convertCrownsToHBT(customerId, amount);
      toast.success(res.data.message);
      setCrownsToConvert('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de conversion');
    }
  };

  // Filtrer les cadeaux
  const getFilteredGifts = () => {
    if (activeTier === 'all') {
      return Object.values(gifts).flat();
    }
    return gifts[activeTier] || [];
  };

  const tabs = [
    { id: 'gifts', name: '🎁 Cadeaux', icon: Gift },
    { id: 'convert', name: '🔄 Convertir', icon: ArrowLeftRight },
    { id: 'history', name: '📜 Historique', icon: History },
  ];

  const tiers = [
    { id: 'all', name: '🌟 Tous' },
    { id: 'bronze', name: '🥉 Bronze' },
    { id: 'silver', name: '🥈 Argent' },
    { id: 'gold', name: '🥇 Or' },
    { id: 'diamond', name: '💎 Diamant' },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl max-h-[90vh] overflow-x-hidden overflow-y-auto ${
        isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white text-gray-900'
      }`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-2xl">💎</span>
            Boutique HBT
            {targetProfile && (
              <span className="text-sm font-normal text-purple-400">
                → Cadeau pour {targetProfile.displayName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Wallet mini */}
            {wallet && (
              <div className={`flex items-center justify-between p-3 rounded-xl ${
                isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
              }`}>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <span>💎</span>
                    <span className="font-bold">{(wallet.balance_hbt || 0).toFixed(1)} HBT</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>👑</span>
                    <span className="font-bold">{wallet.balance_crowns || 0}</span>
                  </div>
                </div>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  ≈ {(wallet.balance_eur || 0).toFixed(2)}€
                </span>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[50vh]">
              {activeTab === 'gifts' && (
                <div className="space-y-4">
                  {/* Tier filter */}
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {tiers.map((tier) => (
                      <button
                        key={tier.id}
                        onClick={() => setActiveTier(tier.id)}
                        className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                          activeTier === tier.id
                            ? `bg-gradient-to-r ${tier.id !== 'all' ? TIER_COLORS[tier.id] : 'from-purple-500 to-pink-500'} text-white`
                            : isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {tier.name}
                      </button>
                    ))}
                  </div>

                  {/* Gifts grid */}
                  <div className="grid grid-cols-2 gap-3">
                    {getFilteredGifts().map((gift) => (
                      <GiftCard
                        key={gift.id}
                        gift={gift}
                        onSelect={setSelectedGift}
                        isDarkMode={isDarkMode}
                        disabled={!targetProfile || (wallet?.balance_hbt || 0) < gift.price_hbt}
                      />
                    ))}
                  </div>

                  {!targetProfile && (
                    <p className={`text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      💡 Sélectionne quelqu'un pour lui envoyer un cadeau !
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'convert' && (
                <div className="space-y-4 p-4">
                  <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <h4 className="font-bold mb-2">🔄 Convertir tes couronnes</h4>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {CROWNS_PER_HBT} 👑 = 1 💎 HBT = {HBT_VALUE_EUR}€
                    </p>
                    
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder={`Min. ${CROWNS_PER_HBT} couronnes`}
                        value={crownsToConvert}
                        onChange={(e) => setCrownsToConvert(e.target.value)}
                        className={isDarkMode ? 'bg-gray-700 border-gray-600' : ''}
                      />
                      <Button
                        onClick={handleConvert}
                        disabled={!crownsToConvert || parseInt(crownsToConvert) < CROWNS_PER_HBT}
                        className="bg-gradient-to-r from-purple-500 to-pink-500"
                      >
                        Convertir
                      </Button>
                    </div>
                    
                    {crownsToConvert && parseInt(crownsToConvert) >= CROWNS_PER_HBT && (
                      <p className={`mt-2 text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                        → {(parseInt(crownsToConvert) / CROWNS_PER_HBT).toFixed(2)} 💎 HBT
                      </p>
                    )}
                  </div>

                  {/* 🔗 Lien d'affiliation MODERNE */}
                  {wallet?.referral_code && (
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                      <h4 className="font-bold mb-2">🔗 Ton lien d'affiliation</h4>
                      <div className={`p-3 rounded-lg font-mono text-xs break-all ${
                        isDarkMode ? 'bg-black/30' : 'bg-white'
                      }`}>
                        https://heartbeats.fr/?ref={wallet.referral_code}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white"
                          onClick={() => {
                            const link = `https://heartbeats.fr/?ref=${wallet.referral_code}`;
                            if (navigator.share) {
                              navigator.share({
                                title: 'Rejoins Heartbeats !',
                                text: '🔥 15 jours GRATUITS sur Heartbeats Premium !',
                                url: link
                              });
                            } else {
                              navigator.clipboard.writeText(link);
                              toast.success('Lien copié !');
                            }
                          }}
                        >
                          📤 Partager
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const link = `https://heartbeats.fr/?ref=${wallet.referral_code}`;
                            navigator.clipboard.writeText(link);
                            toast.success('Lien copié !');
                          }}
                        >
                          📋
                        </Button>
                      </div>
                      <p className={`mt-2 text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                        🎁 5 HBT pour toi, 3 HBT pour ton filleul ! • Tracking 90 jours
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <div className="space-y-2 p-2">
                  {transactions.length === 0 ? (
                    <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Aucune transaction pour le moment
                    </p>
                  ) : (
                    transactions.map((tx, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {tx.description}
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {new Date(tx.createdAt).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <span className={`font-bold ${
                            tx.amount_hbt > 0 ? 'text-green-500' : 'text-red-500'
                          }`}>
                            {tx.amount_hbt > 0 ? '+' : ''}{tx.amount_hbt} 💎
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Send gift dialog */}
            {selectedGift && targetProfile && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl border-2 ${
                  isDarkMode 
                    ? 'bg-purple-900/30 border-purple-500/50' 
                    : 'bg-purple-50 border-purple-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">{selectedGift.emoji}</span>
                  <div>
                    <h4 className="font-bold">{selectedGift.name}</h4>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      → {targetProfile.displayName}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedGift(null)}
                    className={`ml-auto p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <Input
                  placeholder="Ajoute un message... 💜"
                  value={giftMessage}
                  onChange={(e) => setGiftMessage(e.target.value)}
                  className={`mb-3 ${isDarkMode ? 'bg-gray-800 border-gray-700' : ''}`}
                />

                <Button
                  onClick={handleSendGift}
                  disabled={sending}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  {sending ? (
                    <span className="animate-pulse">Envoi en cours...</span>
                  ) : (
                    <>
                      Envoyer pour {selectedGift.price_hbt} 💎
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </motion.div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// 🎊 GIFT RECEIVED ANIMATION
// ============================================
export const GiftReceivedModal = ({ 
  isOpen, 
  onClose, 
  gift, 
  senderName,
  message,
  isDarkMode = true 
}) => {
  if (!gift) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-sm ${
        isDarkMode ? 'bg-gray-900 text-white border-gray-700' : 'bg-white text-gray-900'
      }`}>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', bounce: 0.5 }}
          className="text-center py-6"
        >
          {/* Gift emoji with animation */}
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 10, -10, 0]
            }}
            transition={{ duration: 1, repeat: Infinity }}
            className="text-7xl mb-4"
          >
            {gift.emoji}
          </motion.div>

          <h2 className="text-2xl font-bold mb-2">
            🎁 Tu as reçu un cadeau !
          </h2>

          <p className={`text-lg mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            <span className="font-bold">{gift.name}</span>
          </p>

          <p className={`mb-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
            De la part de <span className="font-bold">{senderName}</span> 💜
          </p>

          {message && (
            <div className={`p-3 rounded-xl mb-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <p className="italic">"{message}"</p>
            </div>
          )}

          <div className={`flex items-center justify-center gap-2 mb-4 ${
            isDarkMode ? 'text-yellow-400' : 'text-yellow-600'
          }`}>
            <Crown className="w-5 h-5" />
            <span className="font-bold">+{gift.reward_crowns} couronnes gagnées !</span>
          </div>

          <Button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          >
            Merci ! 💜
          </Button>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default HBTShopModal;
